import { api } from '@/lib/api';
import { request } from '@/lib/api';
import {
  getMetaPixelUserData,
  hasTrackedMetaPixelEvent,
  markMetaPixelEventTracked,
  trackStartTrial,
} from '@/lib/meta-pixel';
import { CoreValueTrack, buildProjectCreatedProps } from '@seede/amplitude';
import type { ProjectCreatedAttachmentInput, ProjectCreatedSizeInput } from '@seede/amplitude';
import type { CreateProjectRequest } from '@seede/api-client/veeso';
import type { SeedeGraphic } from '@seede/renderer/lib/export';

export function createEmptyProject({ width = 1200, height = 628 }): SeedeGraphic {
  return {
    type: 'seedeGraphic',
    messages: [],
    props: {
      nodes: {
        xd8es1lfb: {
          id: 'xd8es1lfb',
          type: 'frame',
          name: '',
          x: 0,
          y: 0,
          fill: '#ffffff',
          w: width,
          h: height,
          index: 'a0',
        },
      },
    },
  };
}

type CreateProjectPayload = {
  prompt: string;
  tag?: string;
  visibility?: CreateProjectRequest['visibility'];
  width?: number;
  height?: number;
  batchCount?: number;
  model?: string;
  structuredInput?: unknown;
};

type CreateProjectTracking = {
  size?: ProjectCreatedSizeInput;
  attachments?: ProjectCreatedAttachmentInput;
};

export function useProjectCreation() {
  const isSubmitting = ref(false);
  const createProject = async (
    payload: CreateProjectPayload,
    options: {
      creationMode?: 'ai_generate' | 'manual' | 'template';
      scene?: string;
      tracking?: CreateProjectTracking;
    } = {}
  ) => {
    isSubmitting.value = true;

    const { user } = useAuth();
    const startTrialKey = user.value?.id || user.value?.email || '';
    let shouldTrackStartTrial = false;
    let isFirstProject = false;

    // We need `account_first_project` regardless of meta-pixel, so always
    // query when authenticated — but bail silently on error.
    if (startTrialKey) {
      try {
        const projects = (await request('/project', {
          query: {
            limit: '1',
            offset: '0',
          },
        })) as unknown[];
        isFirstProject = Array.isArray(projects) && projects.length === 0;
        if (!hasTrackedMetaPixelEvent('start-trial', startTrialKey)) {
          shouldTrackStartTrial = isFirstProject;
        }
      } catch (error) {
        console.error('Failed to check first project state:', error);
      }
    }

    const projectData = createEmptyProject({ width: payload.width, height: payload.height });

    projectData.props.inputs = {
      prompt: payload.prompt,
      width: payload.width,
      height: payload.height,
      batchCount: payload.batchCount,
      model: payload.model,
    };

    const data = {
      name: '',
      description: '',
      meta: payload.tag
        ? {
            tag: payload.tag,
          }
        : undefined,
      visibility: payload.visibility ?? 'public',
      thumbnailData: '',
      data: btoa(unescape(encodeURIComponent(JSON.stringify(projectData)))),
    };

    return api
      .createProject(data)
      .then((project) => {
        if (shouldTrackStartTrial) {
          trackStartTrial({
            event_id: project.id,
            custom_data: {
              project_id: project.id,
              model: payload.model,
              tag: payload.tag,
            },
            user_data: getMetaPixelUserData(user.value),
          })
            .then(() => {
              markMetaPixelEventTracked('start-trial', startTrialKey);
            })
            .catch((error) => {
              console.error('Failed to track Meta Pixel start trial:', error);
            });
        }

        // Canonical core-value event — replaces the old
        // veeso_landing_create_project_success / veeso_create_design_success.
        // De-duplicated per project, fires $add total_project_created,
        // $setOnce first_project_created_at on first hit.
        try {
          CoreValueTrack.projectCreated({
            project_id: project.id,
            ...buildProjectCreatedProps({
              prompt_content: payload.prompt,
              creation_mode: options.creationMode ?? 'manual',
              scene: options.scene ?? payload.tag,
              account_first_project: isFirstProject,
              coding_model: payload.model,
              visibility: payload.visibility,
              batch_count: payload.batchCount,
              size: {
                width: payload.width,
                height: payload.height,
                ...options.tracking?.size,
              },
              attachments: options.tracking?.attachments,
            }),
          });
        } catch (e) {
          console.error('Failed to track core_value_action_project_created:', e);
        }

        return project;
      })
      .finally(() => {
        isSubmitting.value = false;
      });
  };

  return {
    isSubmitting,
    createProject,
  };
}
